import React, { useRef, useEffect } from 'react';
import videojs from 'video.js';
import Hls from 'hls.js';
import 'video.js/dist/video-js.css';
import './VideoPlayer.scss';
import { v4 as uuidv4 } from 'uuid';

interface VideoPlayerProps {
    videoSource: string;
    isHls?: boolean;
    isRecordingEnabled: boolean;
    completeRecording?: (startTime: number, endTime: number) => void;
    startTimeSeconds?: number;
    endTimeSeconds?: number;
    subtitlesSource?: string;
    hideContextMenu?: boolean;
}

const _HlsDefaultLoader = (Hls as any).DefaultConfig.loader;

function encodeColonsInM3u8(content: string): string {
    return content.split('\n').map(line => {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith('#')) {
            return line;
        }
        if (/^https?:\/\//.test(trimmed)) {
            const authorityEnd = trimmed.indexOf('/', trimmed.indexOf('://') + 3);
            return authorityEnd !== -1
                ? trimmed.substring(0, authorityEnd) + trimmed.substring(authorityEnd).replace(/:/g, '%3A')
                : trimmed;
        }
        return trimmed.replace(/:/g, '%3A');
    }).join('\n');
}

class ColonEncodingLoader {
    private _inner: any;
    constructor(config: any) {
        this._inner = new _HlsDefaultLoader(config);
    }
    get context() { return this._inner.context; }
    get stats() { return this._inner.stats; }
    load(context: any, config: any, callbacks: any) {
        const url: string = context.url || '';
        const authorityEnd = url.indexOf('/', url.indexOf('://') + 3);
        if (authorityEnd !== -1) {
            context = {
                ...context,
                url: url.substring(0, authorityEnd) +
                     url.substring(authorityEnd).replace(/:/g, '%3A'),
            };
        }
        const wrappedCallbacks = {
            ...callbacks,
            onSuccess: (response: any, stats: any, ctx: any, networkDetails: any) => {
                if (typeof response.data === 'string' && /\.m3u8(\?.*)?$/.test(ctx.url || '')) {
                    response = { ...response, data: encodeColonsInM3u8(response.data) };
                }
                callbacks.onSuccess(response, stats, ctx, networkDetails);
            },
        };
        this._inner.load(context, config, wrappedCallbacks);
    }
    abort() { this._inner.abort(); }
    destroy() { this._inner.destroy(); }
}

export const VideoPlayer = (props: VideoPlayerProps) => {
    const guid = 'middleware-' + uuidv4();
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const hlsRef = useRef<Hls | null>(null);

    useEffect(() => {
        if (videoRef.current) {
            const player: any = videojs(videoRef.current, {
                controls: true,
                fluid: true,
                middlewareId:
                    (props.startTimeSeconds || props.startTimeSeconds === 0) &&
                    (props.endTimeSeconds || props.endTimeSeconds === 0)
                        ? guid
                        : undefined,
            });

            if (props.isHls) {
                if (Hls.isSupported()) {
                    const hls = new Hls({
                        xhrSetup: (xhr: XMLHttpRequest) => { xhr.withCredentials = true; },
                        fetchSetup: (_context: any, initParams: any) => {
                            initParams.credentials = 'include';
                            return new Request(_context.url, initParams);
                        },
                        loader: ColonEncodingLoader,
                    });
                    hls.loadSource(props.videoSource);
                    hls.attachMedia(videoRef.current!);
                    hlsRef.current = hls;
                } else if (videoRef.current!.canPlayType('application/vnd.apple.mpegurl')) {
                    player.src({ src: props.videoSource, type: 'application/vnd.apple.mpegurl' });
                }
            } else {
                player.src({ src: props.videoSource, type: 'video/mp4' });
            }

            if (props.subtitlesSource) {
                player.addRemoteTextTrack({
                    src: props.subtitlesSource,
                    kind: 'subtitles',
                    srclang: 'en',
                    label: 'English',
                    default: false,
                });
            }

            if (props.hideContextMenu) {
                const preventContextMenu = (e: Event) => {
                    e.preventDefault();
                    return false;
                };
                
                const disableContextMenuOnElement = (element: HTMLElement) => {
                    if (element) {
                        element.addEventListener('contextmenu', preventContextMenu, true);
                        element.setAttribute('oncontextmenu', 'return false;');
                    }
                };
                
                const playerElement = player.el();
                disableContextMenuOnElement(playerElement);
                playerElement.style.userSelect = 'none';
                
                const videoElement = playerElement.querySelector('video');
                if (videoElement) {
                    disableContextMenuOnElement(videoElement);
                }
                
                player.ready(() => {
                    const controlBar = playerElement.querySelector('.vjs-control-bar');
                    if (controlBar) {
                        disableContextMenuOnElement(controlBar);
                        const allControls = controlBar.querySelectorAll('*');
                        allControls.forEach((control: Element) => {
                            disableContextMenuOnElement(control as HTMLElement);
                        });
                    }
                });
            }

            if (props.isRecordingEnabled) {
                var recordButton = player.controlBar.addChild('button', {}, 1);
                var recordButtonDom = recordButton.el();
                recordButtonDom.title = 'Record';
                recordButton.addClass('vjs-icon-circle-inner-circle');
                let recordingStartTime: any = undefined;
                recordButtonDom.onclick = () => {
                    if (!recordingStartTime && recordingStartTime !== 0) {
                        recordingStartTime = player.currentTime();
                        recordButton.addClass('color-red');
                    } else {
                        recordButton.removeClass('color-red');
                        const currentTime = player.currentTime();
                        if (Math.abs(recordingStartTime - currentTime) < 1) {
                            recordingStartTime = undefined;
                            return;
                        }
                        props.completeRecording(
                            recordingStartTime > currentTime
                                ? currentTime
                                : recordingStartTime,
                            currentTime > recordingStartTime
                                ? currentTime
                                : recordingStartTime
                        );
                        player.pause();
                        recordingStartTime = undefined;
                    }
                };
            }

            if (
                (props.startTimeSeconds || props.startTimeSeconds === 0) &&
                (props.endTimeSeconds || props.endTimeSeconds === 0)
            ) {
                player.on('timeupdate', function () {
                    const currentTime = player.currentTime();
                    if (
                        currentTime >
                        props.endTimeSeconds - props.startTimeSeconds
                    ) {
                        player.currentTime(0);
                        player.pause();
                    }
                });

                let clipMiddleware = function (player: any) {
                    return {
                        setSource(srcObj: any, next: any) {
                            if (player.options().middlewareId === guid) {
                                next(null, srcObj);
                            } else {
                                next(new Error("don't use middleware"));
                            }
                        },
                        currentTime: function (ct: any) {
                            return ct - props.startTimeSeconds;
                        },
                        duration: function () {
                            return (
                                props.endTimeSeconds - props.startTimeSeconds
                            );
                        },
                        setCurrentTime: function (time: any) {
                            return time + props.startTimeSeconds;
                        },
                    };
                };

                videojs.use('*', clipMiddleware);
                player.currentTime(0);
            }

            return () => {
                hlsRef.current?.destroy();
                hlsRef.current = null;
                if (player) {
                    player.dispose();
                }
            };
        }
    }, [props.videoSource]);

    return props.videoSource ? (
        <div className={`video-player ${props.hideContextMenu ? 'hide-context-menu' : ''}`}>
            <video
                ref={videoRef}
                className="video-js"
                data-setup='{"playbackRates": [0.25, 0.5, 1, 1.5, 2]}'
            />
        </div>
    ) : (
        <></>
    );
};

export default VideoPlayer;
